import React from "react";
import Chart from "react-google-charts";
import { MarkedForTestNoTestsVisual } from "./MarkedForTestNoTestsVisual";
import { TopTestedVisual } from "./TopTestedVisual";
import { NoAssayNoTestsVisual } from "./NoAssayNoTestsVisual";
import { CatalogAssayWithoutVisual } from "./CatalogAssayWithoutVisual";

const CHART_COLORS = ["#c82534", "#00965a", "#2c6fbb", "#e8a317", "#6b4c9a"];

const baseOptions = {
  legend: { position: "bottom", textStyle: { fontSize: 12 } },
  chartArea: { width: "82%", height: "68%" },
  hAxis: { textStyle: { fontSize: 11 } },
  vAxis: { textStyle: { fontSize: 11 }, minValue: 0 },
  colors: CHART_COLORS,
};

function TypeBarChart({ title, byType, valueKey = "count" }) {
  const items = byType || [];
  if (!items.length) return null;

  const data = [
    ["Type", "Count"],
    ...items.map((r) => [r.type, r[valueKey] ?? r.count ?? 0]),
  ];

  return (
    <div className="converter-testing-chart-card">
      <div className="converter-testing-chart-title">{title}</div>
      <Chart
        chartType="ColumnChart"
        width="100%"
        height="280px"
        data={data}
        options={{
          ...baseOptions,
          colors: [CHART_COLORS[0]],
          legend: { position: "none" },
        }}
        loader={<div className="c-gray fs-13">Loading chart...</div>}
      />
    </div>
  );
}

function DonutChart({ title, labels, values }) {
  if (!labels?.length || !values?.some((v) => v > 0)) return null;

  const data = [["Label", "Count"], ...labels.map((label, i) => [label, values[i] ?? 0])];

  return (
    <div className="converter-testing-chart-card">
      <div className="converter-testing-chart-title">{title}</div>
      <Chart
        chartType="PieChart"
        width="100%"
        height="280px"
        data={data}
        options={{
          ...baseOptions,
          pieHole: 0.45,
          colors: CHART_COLORS,
        }}
        loader={<div className="c-gray fs-13">Loading chart...</div>}
      />
    </div>
  );
}

function MonthTrendChart({ title, byMonth }) {
  const items = byMonth || [];
  if (!items.length) return null;

  const data = [["Month", "Marked"], ...items.map((r) => [r.month, r.count ?? 0])];

  return (
    <div className="converter-testing-chart-card">
      <div className="converter-testing-chart-title">{title}</div>
      <Chart
        chartType="AreaChart"
        width="100%"
        height="280px"
        data={data}
        options={{
          ...baseOptions,
          colors: [CHART_COLORS[0]],
          legend: { position: "none" },
          areaOpacity: 0.2,
        }}
        loader={<div className="c-gray fs-13">Loading chart...</div>}
      />
    </div>
  );
}

function CoverageStackedChart({ byType }) {
  const items = byType || [];
  if (!items.length) return null;

  const data = [
    ["Type", "Tested", "Untested"],
    ...items.map((r) => [r.type, r.tested ?? 0, r.untested ?? 0]),
  ];

  return (
    <div className="converter-testing-chart-card">
      <div className="converter-testing-chart-title">Tested vs untested by type</div>
      <Chart
        chartType="ColumnChart"
        width="100%"
        height="300px"
        data={data}
        options={{
          ...baseOptions,
          isStacked: true,
          colors: ["#00965a", "#d9d9d9"],
        }}
        loader={<div className="c-gray fs-13">Loading chart...</div>}
      />
    </div>
  );
}

function CatalogAssayStackedChart({ byType }) {
  const items = byType || [];
  if (!items.length) return null;

  const data = [
    ["Type", "With assay", "Without assay"],
    ...items.map((r) => [r.type, r.withAssay ?? 0, r.withoutAssay ?? 0]),
  ];

  return (
    <div className="converter-testing-chart-card">
      <div className="converter-testing-chart-title">With vs without catalog assay by type</div>
      <Chart
        chartType="ColumnChart"
        width="100%"
        height="300px"
        data={data}
        options={{
          ...baseOptions,
          isStacked: true,
          colors: ["#00965a", "#c82534"],
        }}
        loader={<div className="c-gray fs-13">Loading chart...</div>}
      />
    </div>
  );
}

function KpiStrip({ items }) {
  if (!items?.length) return null;
  return (
    <div className="converter-testing-kpi-row">
      {items.map((item) => (
        <div key={item.label} className="converter-testing-kpi">
          <div className="converter-testing-kpi-value">{item.value}</div>
          <div className="converter-testing-kpi-label">{item.label}</div>
        </div>
      ))}
    </div>
  );
}

export function ReportExecutivePanel({ reportType, summary }) {
  if (!summary) return null;

  const byType = summary.byType || [];
  const totalCount = summary.totalCount ?? summary.total ?? 0;

  const kpis = [{ label: "Total", value: totalCount.toLocaleString() }];

  if (reportType === "markedForTest") {
    kpis.push(
      { label: "With tests", value: (summary.withTests ?? 0).toLocaleString() },
      { label: "No tests yet", value: (summary.withoutTests ?? 0).toLocaleString() }
    );
  } else if (reportType === "topTested") {
    kpis.push({ label: "Test records", value: (summary.totalTestRecords ?? 0).toLocaleString() });
  } else if (reportType === "noAssay") {
    kpis.push(
      { label: "Has tests", value: (summary.withTests ?? 0).toLocaleString() },
      { label: "No tests yet", value: (summary.withoutTests ?? 0).toLocaleString() }
    );
  }

  return (
    <div className="converter-testing-executive">
      <div className="converter-testing-executive-title">Executive summary</div>
      <KpiStrip items={kpis} />
      <div className="converter-testing-charts-row">
        <TypeBarChart title="By converter type" byType={byType} />
        {reportType === "markedForTest" && (
          <>
            <DonutChart
              title="Testing status"
              labels={["Has test records", "No test records"]}
              values={[summary.withTests ?? 0, summary.withoutTests ?? 0]}
            />
            <MonthTrendChart title="Marked over time" byMonth={summary.byMonth} />
          </>
        )}
        {reportType === "topTested" && (
          <DonutChart
            title="Share by type"
            labels={byType.map((r) => r.type)}
            values={byType.map((r) => r.count ?? 0)}
          />
        )}
        {reportType === "noAssay" && (
          <DonutChart
            title="Test status"
            labels={["Has tests — needs catalog update", "No tests yet"]}
            values={[summary.withTests ?? 0, summary.withoutTests ?? 0]}
          />
        )}
      </div>
      {reportType === "markedForTest" && (
        <MarkedForTestNoTestsVisual summary={summary} />
      )}
      {reportType === "topTested" && (
        <TopTestedVisual summary={summary} />
      )}
      {reportType === "noAssay" && (
        <NoAssayNoTestsVisual summary={summary} />
      )}
    </div>
  );
}

export function CatalogAssayExecutivePanel({ summary }) {
  if (!summary) return null;
  const byType = summary.byType || [];

  return (
    <div className="converter-testing-executive">
      <div className="converter-testing-executive-title">Executive summary</div>
      <div className="converter-testing-charts-row">
        <CatalogAssayStackedChart byType={byType} />
        <DonutChart
          title="Overall catalog assay status"
          labels={["With catalog assay", "Without catalog assay"]}
          values={[summary.withAssay ?? 0, summary.withoutAssay ?? 0]}
        />
      </div>
      <CatalogAssayWithoutVisual summary={summary} />
    </div>
  );
}

export function CoverageExecutivePanel({ summary }) {
  if (!summary) return null;
  const byType = summary.byType || [];

  return (
    <div className="converter-testing-executive">
      <div className="converter-testing-executive-title">Executive summary</div>
      <div className="converter-testing-charts-row">
        <CoverageStackedChart byType={byType} />
        <DonutChart
          title="Overall testing status"
          labels={["Tested", "Untested"]}
          values={[summary.tested ?? 0, summary.untested ?? 0]}
        />
      </div>
    </div>
  );
}
